import { ReactNode } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";

const { color, typeScale } = adminTokens;
const [spaceXs, , , spaceLg] = adminTokens.spacing;
// Below this the title column wraps the right-side content onto its own row.
const titleColumnMinWidth = 260;
// On a phone the title is Light 24 and the right-side content takes its own row
// (PHONE-R26, PHONE-R27).
const phoneTitleSize = "24px";

// The title and the right-side content share a row and wrap onto two rows when
// the width cannot hold both (HEAD-R3).
const Header = styled.header<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: ${({ $isPhone }) => ($isPhone ? "column" : "row")};
  flex-wrap: wrap;
  align-items: ${({ $isPhone }) => ($isPhone ? "stretch" : "flex-end")};
  gap: ${spaceLg}px;
`;

const Titles = styled.div<{ $isPhone: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spaceXs}px;
  min-width: ${({ $isPhone }) => ($isPhone ? 0 : titleColumnMinWidth)}px;
`;

const Title = styled.h1<{ $isPhone: boolean }>`
  margin: 0;
  color: ${color.navy};
  font-family: ${typeScale.pageTitle.fontFamily};
  font-size: ${({ $isPhone }) => ($isPhone ? phoneTitleSize : typeScale.pageTitle.fontSize)};
  overflow-wrap: ${({ $isPhone }) => ($isPhone ? "anywhere" : "normal")};
  font-weight: normal;
  line-height: ${typeScale.pageTitle.lineHeight};
`;

const Subtitle = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
  line-height: ${typeScale.body.lineHeight};
`;

interface PageHeaderProps {
  title: string;
  titleLevel?: 1 | 2 | 3;
  subtitle?: ReactNode;
  aside?: ReactNode;
}

// The top of every admin screen: the page's one level 1 heading, an optional
// subtitle and an optional right side for tabs or a search (HEAD-R1…R4). The
// title is the page's level 1 heading unless the screen gives another level.
export const PageHeader = ({ title, titleLevel = 1, subtitle, aside }: PageHeaderProps) => {
  const isPhone = useIsPhoneLayout();

  return (
    <Header $isPhone={isPhone}>
      <Titles $isPhone={isPhone}>
        <Title as={`h${titleLevel}` as "h1" | "h2" | "h3"} $isPhone={isPhone}>
          {title}
        </Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Titles>
      {aside}
    </Header>
  );
};
